import { observable, action, computed } from "mobx";
import Alert from "./Alert";
import Api from "../service/Api";
import BindDataToStore from "../utils/BindDataToStore";
import TourProduct from "../models/TourProduct";

// Translate 'tour publish' action (publish tour, invoice, ransom, charge) (since it's a bit hard to read)
// to labels that we can leverage in this widget.
// @TODO Would be cool to clean up names in the backend.
const getNormalizedAction = suggestedAction => {
  // Translate backend action to the action we use in the frontend.
  // Note: available frontend actions are 'PUBLISH', 'PUBLISH_WITH_INVOICE', 'RANSOM', 'CHARGE'.
  switch (suggestedAction) {
    case null:
    case "":
    case "none":
      return "PUBLISH";
    // no break
    case "invoice":
      return "PUBLISH_WITH_INVOICE";
    // no break
    case "require":
      return "RANSOM";
    // no break
    case "cod":
      return "CHARGE";
    // no break
    default:
      return "PUBLISH";
  }
};

/**
 * Get the final computed publishing Action for the tour. Even if the backend suggests a given Action it might be
 * possible that the current tour state won't allow using it, so we need to take into account this and make sure
 * we show an appropriated Action.
 *
 * @param {string} suggestedAction The preferred course of action for publishing the tour.
 * @param {object} tourState
 * @returns string
 */
const getSuitableAction = (
  suggestedAction,
  { hasInvoicingSystem, hasStripe, isStripeDown, canCollectPayment, canConnectToStripe, creditCards },
) => {
  // Let's make sure that the action can actually be executed. For example, if the customer selected option
  // is to charge card on file, but he has no cards, then that makes the action to be impossible to fulfill.

  let actualAction = suggestedAction;

  // If the action is to invoice, but there is no invoice system...
  if (actualAction === "PUBLISH_WITH_INVOICE" && !hasInvoicingSystem) {
    actualAction = "PUBLISH";
  }

  // If the action is to ransom or to charge,
  if (actualAction === "RANSOM" || actualAction === "CHARGE") {
    // If there's no stripe...
    if (!hasStripe) {
      actualAction = "PUBLISH";
    }

    // If Stripe is down.
    if (isStripeDown) {
      actualAction = "PUBLISH";
    }

    // If user can't collect payment
    if (!canCollectPayment) {
      actualAction = "PUBLISH";
    }

    // If user don't have stripe already and cannot connect to stripe...
    if (!canConnectToStripe) {
      actualAction = "PUBLISH";
    }
  }

  // If the action is to charge card on file, but the customer doesn't actually have cards...
  if (actualAction === "CHARGE" && creditCards.length === 0) {
    actualAction = "PUBLISH";
  }

  return actualAction;
};

// Currently this is the form that shows up in the tour edit page.
// When a provider has "ransom" feature enabled, and when the tour is "ransomable", instead
// of showing the standard "publish tour" and "publish photos only" we'll show up a single
// "Deliver" button instead, that when clicked we'll show up a modal form where the user can
// set the price of the ransom (if they choses to).
export default class TourDeliver {
  @observable tourId = -1;

  @observable tourNotes = "";

  @observable customerNotes = "";

  @observable isStripeDown = false;

  @observable stripeAuthUrl = "";

  @observable hasInvoicingSystem = false;

  @observable invoicingSystemTitle = "";

  @observable businessSolutionUrl = "";

  @observable isBusinessSolutionManaged = false;

  // If the tour is ransomed already or not. Will be used to toggle the react component between the
  // Ransom form and the "cancel ransom and publish" components.
  @observable isRansom = false;

  @observable tourProducts = [];

  @observable isDelivered = false;

  @observable requiresRansomPayment = false;

  @observable saving = false;

  @observable invoiceId = "";

  @observable amountDue = "";

  // A list of possible actions that the user can take. The user might decide to publish the tour
  // (either fully or photos only); or to ransom it; or to charge for the tour (when a credit card is available).
  actions = ["PUBLISH", "PUBLISH_WITH_INVOICE", "RANSOM", "CHARGE"];
  // 'PUBLISH' is the default since it's the least aggressive strategy.

  @observable action = "PUBLISH";

  @observable hasDefaultPaymentCollectionMethod = false;

  @observable savePaymentCollectionMethod = false;

  @observable doNotShowSavePaymentCollectionMethodPrompt = false;

  @observable creditCards = [];

  @observable creditCard = { id: "", brand: "", last4: "" };

  @observable currencies = [];

  @observable currency = "";

  @observable description = "";

  @observable handoffNotes = "";

  @observable auditLogs = [];

  @observable ransomErrors = {
    amountDue: "",
  };

  @observable alertStore;

  // Permissions
  @observable canConnectToStripe = false;

  @observable canPublishTour = false;

  @observable canCollectPayment = false;

  // alert boxes (confirmations and prompts)
  @observable showSaveDefaultPaymentCollectionPrompt = false;

  @observable showRansomRemovalWarning = false;

  @observable showRedirectToBusinessSolutionConfirm = false;

  constructor(data) {
    const { action: suggestedAction, selectedProductId, products, ...rest } = data;
    const normalizedAction = getNormalizedAction(suggestedAction);
    const suitableAction = getSuitableAction(normalizedAction, rest);
    this.action = suitableAction;

    this.receiveTourProducts(products, selectedProductId);
    this.setSelectedTourProduct(selectedProductId);

    BindDataToStore(this, rest);
    this.alertStore = new Alert({});
  }

  @action receiveTourProducts(products) {
    products.forEach(product => {
      const tourProduct = new TourProduct(product);
      this.tourProducts.push(tourProduct);
    });
  }

  @action setSelectedTourProduct(selectedProductId) {
    this.tourProducts.forEach(tourProduct => {
      tourProduct.setIsSelected(tourProduct.productId === selectedProductId);
    });
  }

  getSelectedProduct() {
    const selectedProduct = this.tourProducts.find(tourProduct => tourProduct.isSelected);

    if (!selectedProduct) {
      throw new Error("Expected to find a selected product");
    }

    return selectedProduct;
  }

  @action setCurrency(currency) {
    if (this.currencies.indexOf(currency) === -1) {
      throw new Error(`currency ${currency} not supported`);
    }

    this.currency = currency;
  }

  @action setAction(userAction) {
    if (this.actions.indexOf(userAction) === -1) {
      throw new Error(`action ${userAction} not supported`);
    }

    this.action = userAction;
  }

  @action setSavePaymentCollectionMethod(savePaymentCollectionMethod) {
    this.savePaymentCollectionMethod = savePaymentCollectionMethod;
  }

  @action setHasDefaultPaymentCollectionMethod(hasDefaultPaymentCollectionMethod) {
    this.hasDefaultPaymentCollectionMethod = hasDefaultPaymentCollectionMethod;
  }

  @action toggleDoNotShowSavePaymentCollectionMethodPrompt() {
    this.doNotShowSavePaymentCollectionMethodPrompt = !this.doNotShowSavePaymentCollectionMethodPrompt;
  }

  @action setCreditCard(selectedCreditCardId) {
    let creditCard = null;

    for (let i = 0; i < this.creditCards.length; i += 1) {
      if (this.creditCards[i].id === selectedCreditCardId) {
        creditCard = this.creditCards[i];
        break;
      }
    }

    if (!creditCard) {
      throw new Error(`Credit card with Id ${selectedCreditCardId} not found`);
    }

    this.creditCard = creditCard;
  }

  @action setSaving(saving) {
    this.saving = saving;
  }

  @action setInvoiceId(invoiceId) {
    this.invoiceId = invoiceId;
  }

  @action setAmountDue(amountDue) {
    this.amountDue = amountDue;
  }

  @action setDescription(description) {
    this.description = description;
  }

  @action setHandoffNotes(handoffNotes) {
    this.handoffNotes = handoffNotes;
  }

  @computed get isDeliverManagerVisible() {
    // If the tour is ransomed already and the user cannot collect payment,  then we should not show
    // the Deliver Manager widget in order to avoid the user from upgrading/releasing the tour by mistake.
    if (this.requiresRansomPayment && !this.canCollectPayment) {
      return false;
    }

    return true;
  }

  @computed get actionWillCancelRansom() {
    return this.requiresRansomPayment && this.action !== "RANSOM";
  }

  @computed get actionWillUpgradeProduct() {
    // Find a paid product.
    const paidProduct = this.tourProducts.find(tourProduct => tourProduct.isPaid);

    // if no paid product, then there's no upgrade.
    if (!paidProduct) {
      return false;
    }

    // find the current selected product.
    const selectedProduct = this.getSelectedProduct();

    if (!selectedProduct) {
      throw new Error("Expected to find a selected product");
    }

    // if the current product is the same that has been paid, then there's no upgrade
    if (selectedProduct.productId === paidProduct.productId) {
      return false;
    }

    // there's an upgrade if the tier is greater or equal. We allow equal because it can be another product
    // on the same tier.

    return selectedProduct.tier >= paidProduct.tier;
  }

  @computed get ransomDisabled() {
    return !this.hasStripe || this.isDelivered;
  }

  @computed get deliverButtonLabel() {
    if (this.actionWillUpgradeProduct && this.actionWillCancelRansom) {
      return "Upgrade & Release";
    }

    if (this.actionWillUpgradeProduct) {
      return "Upgrade & Deliver";
    }

    if (this.actionWillCancelRansom) {
      return "Release & Deliver";
    }

    return "Deliver";
  }

  /**
   * If the tour is already published or requires ransom payment, get an url
   * to skip directly to the email notification page.
   */
  @computed get skipToEmailNotification() {
    if (this.requiresRansomPayment) {
      return this.getRansomRedirectUrl();
    }

    const publishedProduct = this.tourProducts.find(tourProduct => tourProduct.isPaid);

    if (publishedProduct) {
      // return a one-for-all announce email notification controller that should handle each
      // specific product.
      return `/panel/tour/announce_delivery/${this.tourId}/${publishedProduct.productId}`;
    }

    return null;
  }

  @computed get isStripeTeaseVisible() {
    return !this.hasStripe && this.canConnectToStripe;
  }

  @computed get isPaymentCollectionVisible() {
    return this.canCollectPayment || this.canConnectToStripe;
  }

  @computed get customerHasCreditCards() {
    return this.creditCards.length > 0;
  }

  @computed get isAbleToChargeCardOnFile() {
    // only show this warning if the user is provisioned in stripe and can make use of the service.
    //  Note checking for 'hasStripe' might be redundant if we are also checking for 'canCollectPayment'
    // but let's do both in case in the future we allow providers to be provisioned in Stripe but we revoke their
    // permission to make use of it.

    // User has a stripe connect record in db and has the permission to collectPayment, and the customer does have
    // credit cards on file
    return this.hasStripe && this.canCollectPayment && this.creditCards.length > 0;
  }

  @computed get shouldDisplayStripeIsDownWarning() {
    return this.isAbleToChargeCardOnFile && this.isStripeDown;
  }

  @action shouldShowSaveDefaultPaymentCollectionPrompt() {
    // Prompt should show up only on tours in progress.
    if (this.isDelivered) {
      return false;
    }

    // Prompt is part of the Collection Payment feature.
    if (!this.canCollectPayment) {
      return false;
    }

    // Don't ask if customer already has a default method
    if (this.hasDefaultPaymentCollectionMethod) {
      return false;
    }

    // Display form errors and bail showing the prompt (if any)
    if (!this.validateRansomForm()) {
      return false;
    }

    return true;
  }

  /**
   * @param {boolean} value
   */
  @action setShowSaveDefaultPaymentCollectionPrompt(value) {
    this.showSaveDefaultPaymentCollectionPrompt = value;
  }

  /**
   * @param {boolean} value
   */
  @action setShowRansomRemovalWarning(value) {
    this.showRansomRemovalWarning = value;
  }

  /**
   * @param {boolean}
   */
  @action setShowRedirectToBusinessSolutionConfirm(value) {
    this.showRedirectToBusinessSolutionConfirm = value;
  }

  /**
   * @return Promise
   */
  @action submitRansom() {
    if (this.saving) {
      // prevent re-submit.
      return Promise.resolve();
    }

    if (!this.validateRansomForm()) {
      return Promise.resolve();
    }

    this.saving = true;

    let invoiceId;

    // If the user chose to publish only (no invoice), then we won't show the invoice form hence it's assumed
    // that the invoice id is not provided.
    if (this.action === "PUBLISH") {
      invoiceId = null;
    } else {
      invoiceId = this.invoiceId;
    }

    const selectedProduct = this.getSelectedProduct();

    // If you add/remove/rename any of these properties make sue to also update TourDeliveryPayload.php.
    const payload = {
      productId: selectedProduct.productId,
      invoiceId,
      amountDue: this.amountDue,
      action: this.action,
      creditCardId: this.creditCard ? this.creditCard.id : null,
      chargeDescription: this.description,
      currency: this.currency,
      handoffNotes: this.handoffNotes,
      savePaymentCollectionMethod: this.savePaymentCollectionMethod,
      doNotShowSavePaymentCollectionMethodPrompt: this.doNotShowSavePaymentCollectionMethodPrompt,
    };

    let postUrl;
    let redirectUrl;

    // Note the if else blocks below, where we check action equals PUBLISH or PUBLISH_WITH_INVOICE. If the user choses
    // PUBLISH then we won't show the invoiceId input, so that translates to "publish tour without providing a invoice
    // id". If they choose 'PUBLISH_WITH_INVOICE' then we show an invoiceId input, it translates to "publish tour while
    // providing an invoice id". Note that both actions publish the tour immediately.

    const publishAction = this.action === "PUBLISH" || this.action === "PUBLISH_WITH_INVOICE";

    if (!this.canPublishTour) {
      postUrl = this.getHandoffNotesUrl();
      redirectUrl = this.getHandoffNotesRedirectUrl();
    } else if (this.action === "RANSOM") {
      postUrl = this.getDeliverUrl();
      redirectUrl = this.getRansomRedirectUrl();
    } else if (publishAction || this.action === "CHARGE") {
      postUrl = this.getDeliverUrl();
      redirectUrl = this.getDeliverRedirectUrl();
    } else {
      throw new Error(
        `Unexpected error. Action and product did not match ${this.action} - ${selectedProduct.productId}`,
      );
    }

    return Api.post(postUrl, payload)
      .then(response => {
        // Even if we got a 202 response, it doesn't necessary mean that the tour got delivered.
        // It could happened that the user had not enough credits. In that case we'd redirect
        // the user to the panel/credits page (if possible).

        if (response.success) {
          // Redirect to the next screen. If the response has a specific url,
          // then use it, otherwise use the default one.
          window.location = response.redirectUrl || redirectUrl;
        } else {
          // If there was an expected error, let's the user know.
          alert(response.message);
          // An error can imply needing to redirect the user (like when out of credits).
          if (response.redirectUrl) {
            window.location = response.redirectUrl;
          }
        }
      })
      .catch(err => {
        if (typeof err.message !== "undefined") {
          alert(err.message);
        } else {
          alert("An unexpected error occurred. Please try later.");
        }

        this.saving = false;
        throw err;
      });
  }

  // Validates the ransom form and mutate errors so they show up in the UI.
  @action validateRansomForm() {
    let ransomErrors = {};
    // default data.
    ransomErrors = {
      amountDue: "",
    };

    let valid = true;

    // Validate amountDue input only if the user decides to ransom or charge.
    if (this.action === "RANSOM" || this.action === "CHARGE") {
      if (!this.amountDue) {
        ransomErrors.amountDue = "amount due is required";
        valid = false;
      }

      if (this.amountDue) {
        const match = /^\d+(\.\d{1,2})?$/;
        if (!match.test(this.amountDue)) {
          ransomErrors.amountDue = "enter a valid amount due";
          valid = false;
        } else if (!(this.amountDue >= 0.5)) {
          // if it's a valid number, make sure it's greater than 50 cents.
          ransomErrors.amountDue = "amount due must be greater than or equal to 0.50 cents.";
          valid = false;
        }
      }
    }

    this.ransomErrors = ransomErrors;

    return valid;
  }

  // URL STUFF

  // Once the tour has been ransomed, redirect to this url.
  getRansomRedirectUrl() {
    return `/panel/tour/ransom_notification/${this.tourId}`;
  }

  // Post to this url for all purpose actions.
  getDeliverUrl() {
    return `/tours/${this.tourId}/deliver`;
  }

  // Redirect to this url once a tour has been published (no ransom).
  getDeliverRedirectUrl() {
    const selectedProduct = this.getSelectedProduct();

    return `/panel/tour/announce_delivery/${this.tourId}/${selectedProduct.productId}`;
  }

  getHandoffNotesUrl() {
    return `/tours/${this.tourId}/handoff`;
  }

  getHandoffNotesRedirectUrl() {
    return `/panel/tour/providerNotified/${this.tourId}`;
  }

  @action
  publishBusinessSolutionManagedTour() {
    this.saving = true;

    return Api.post(`/tours/${this.tourId}/publish-business-solution`)
      .then(response => {
        if (response.success) {
          // Redirect to the next screen. If the response has a specific url,
          // then use it, otherwise use the default one.
          window.location = this.businessSolutionUrl;
        } else {
          const error = response.message || "invalid response";
          alert(`An unexpected error ocurred: ${error}`);
        }
      })
      .catch(err => {
        if (typeof err.message !== "undefined") {
          alert(err.message);
        } else {
          alert("An unexpected error occurred. Please try later.");
        }

        this.saving = false;
        throw err;
      });
  }
}
